/**
 * AuctionDetailV2Page — Phase 7
 * View live auction, place bid / auto-bid, buy now.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Gavel, ShieldCheck, TrendingUp, Zap } from "lucide-react";

import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import {
  auctionsAPI, AUCTION_STATUS_LABELS, formatCountdown,
} from "../../lib/auctionsV2";

export default function AuctionDetailV2Page() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();

  const [a, setA] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  const pollRef = useRef();

  const load = useCallback(async () => {
    try {
      const [au, bh] = await Promise.all([
        auctionsAPI.get(id), auctionsAPI.bids(id, { limit: 30 }),
      ]);
      setA(au);
      setBids(bh.items || []);
      // Pre-fill minimum bid
      const minBid = (au.bid_count > 0
        ? Number(au.current_price_sar) + Number(au.bid_increment_sar)
        : Number(au.starting_price_sar));
      setAmount(String(minBid));
    } catch {
      toast.error(lang === "ar" ? "تعذّر تحميل المزاد" : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id, lang]);

  useEffect(() => { load(); }, [load]);

  // Poll every 5s while live
  useEffect(() => {
    if (!a) return;
    if (!["live", "extended"].includes(a.status)) return;
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [a, load]);

  // Countdown tick
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const placeBid = async (e) => {
    e.preventDefault();
    if (!user) { toast.error(lang === "ar" ? "سجّل الدخول أولاً" : "Log in first"); navigate("/login"); return; }
    if (!amount) return;
    setBusy(true);
    try {
      await auctionsAPI.bid(id, {
        amount_sar: Number(amount),
        max_amount_sar: maxAmount ? Number(maxAmount) : undefined,
      });
      toast.success(lang === "ar" ? "تم استلام مزايدتك" : "Bid placed");
      setMaxAmount("");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.detail || (lang === "ar" ? "فشل المزايدة" : "Bid failed")));
    } finally {
      setBusy(false);
    }
  };

  const buyNow = async () => {
    if (!user) { toast.error(lang === "ar" ? "سجّل الدخول أولاً" : "Log in first"); navigate("/login"); return; }
    if (!window.confirm(lang === "ar" ? `تأكيد الشراء الفوري بمبلغ ${Number(a.buy_now_price_sar).toLocaleString()} ر.س؟` : "Confirm Buy Now?")) return;
    setBusy(true);
    try {
      const res = await auctionsAPI.buyNow(id);
      toast.success(lang === "ar" ? "تم الشراء — انتقل للمعاملة" : "Purchased — going to transaction");
      navigate(`/transactions/${res.transaction.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "فشل الشراء" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="min-h-[70vh] flex items-center justify-center text-[#8a8a8a]">…</div>;
  if (!a) return <div className="min-h-[70vh] flex items-center justify-center">Not found</div>;

  const label = AUCTION_STATUS_LABELS[a.status] || { ar: a.status, en: a.status };
  const isLive = a.status === "live" || a.status === "extended";
  const minBid = (a.bid_count > 0
    ? Number(a.current_price_sar) + Number(a.bid_increment_sar)
    : Number(a.starting_price_sar));

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#faf9f5]" dir={lang === "ar" ? "rtl" : "ltr"}
         data-testid="auction-detail-v2">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Main */}
        <div className="space-y-5">
          <div className="rounded-2xl overflow-hidden bg-white border border-[#0a0a0a]/[0.06]">
            <div className="aspect-[16/10] bg-[#f0eee7]">
              {a.images?.[0] ? (
                <img src={a.images[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Gavel className="w-16 h-16 opacity-20" />
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {isLive && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-red-500 animate-pulse">
                    {lang === "ar" ? "مباشر" : "LIVE"}
                  </span>
                )}
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#0a0a0a] text-[#f7eecd]">
                  {lang === "ar" ? label.ar : label.en}
                </span>
                <span className="text-[11px] text-[#8a8a8a]">
                  #{a.id.slice(-8)}
                </span>
              </div>
              <h1 className="text-2xl font-display font-bold text-[#0a0a0a] mb-2">
                {lang === "ar" ? a.title_ar : (a.title_en || a.title_ar)}
              </h1>
              {a.description_ar && (
                <p className="text-sm text-[#525252] whitespace-pre-wrap">{lang === "ar" ? a.description_ar : (a.description_en || a.description_ar)}</p>
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5">
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
              {lang === "ar" ? "سجل المزايدات" : "Bid history"} ({bids.length})
            </div>
            {bids.length === 0 ? (
              <div className="text-sm text-[#8a8a8a]">
                {lang === "ar" ? "لا توجد مزايدات بعد" : "No bids yet"}
              </div>
            ) : (
              <div className="divide-y divide-[#0a0a0a]/[0.06]">
                {bids.map((b) => (
                  <div key={b.id} className="py-2 flex items-center justify-between text-[13px]"
                       data-testid={`bid-row-${b.id}`}>
                    <div className="inline-flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#f0eee7] flex items-center justify-center text-[10px] font-bold text-[#8a6f2e]">
                        {b.user_id.slice(0, 2).toUpperCase()}
                      </div>
                      <span>{b.user_id.slice(0, 8)}…</span>
                      {b.is_auto && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">AUTO</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-bold ${b.status === "winning" ? "text-emerald-700" : b.status === "outbid" ? "text-[#8a8a8a] line-through" : "text-[#0a0a0a]"}`}>
                        {Number(b.amount_sar).toLocaleString()} ر.س
                      </span>
                      <span className="text-[10px] text-[#8a8a8a]">
                        {new Date(b.created_at).toLocaleTimeString(lang === "ar" ? "ar-SA" : "en-US")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bid panel */}
        <div className="space-y-4 lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl overflow-hidden shadow-md"
               style={{ background: "linear-gradient(180deg,#0a0a0a,#1f1614)" }}>
            <div className="p-5 text-[#f7eecd]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#c9a55c] font-bold mb-1">
                {lang === "ar" ? "السعر الحالي" : "Current Price"}
              </div>
              <div className="text-3xl font-bold mb-3" data-testid="auc-current-price">
                {Number(a.current_price_sar).toLocaleString()} <span className="text-sm">SAR</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center mb-4">
                <Stat lbl={lang === "ar" ? "المزايدات" : "Bids"} val={a.bid_count} />
                <Stat lbl={lang === "ar" ? "المزايدون" : "Bidders"} val={a.bidder_count} />
                <Stat lbl={lang === "ar" ? "المشاهدات" : "Views"} val={a.view_count} />
              </div>
              {isLive && (
                <div className="flex items-center gap-2 mb-4 p-2 bg-red-500/20 rounded-lg">
                  <Clock className="w-4 h-4 text-red-300" />
                  <span className="text-[12px] font-bold text-red-200" data-testid="auc-countdown">
                    {formatCountdown(a.ends_at)}
                  </span>
                </div>
              )}

              {isLive ? (
                <form onSubmit={placeBid} className="space-y-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#c9a55c] font-bold">
                      {lang === "ar" ? "مزايدتك (الحد الأدنى" : "Your bid (min"} {Number(minBid).toLocaleString()})
                    </label>
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                           min={minBid} step={a.bid_increment_sar}
                           className="w-full mt-1 px-3 py-2 rounded-lg bg-white/[0.08] text-[#f7eecd] border border-white/20 focus:outline-none focus:border-[#c9a55c]"
                           data-testid="bid-amount-input" required />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#c9a55c] font-bold inline-flex items-center gap-1">
                      <Zap className="w-3 h-3" /> {lang === "ar" ? "الحد الأقصى (اختياري)" : "Auto-bid cap (optional)"}
                    </label>
                    <input type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)}
                           min={minBid} step={a.bid_increment_sar}
                           placeholder={lang === "ar" ? "المزايدة التلقائية" : "Enable auto-bidding"}
                           className="w-full mt-1 px-3 py-2 rounded-lg bg-white/[0.08] text-[#f7eecd] border border-white/20 focus:outline-none focus:border-[#c9a55c]"
                           data-testid="bid-max-input" />
                  </div>
                  <button type="submit" disabled={busy}
                          className="w-full mt-2 py-3 rounded-full font-bold text-[#0a0a0a] disabled:opacity-40"
                          style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}
                          data-testid="bid-submit">
                    {busy ? "…" : (lang === "ar" ? "زايد الآن" : "Place Bid")}
                  </button>
                </form>
              ) : (
                <div className="p-3 text-center text-[12px] text-[#c9a55c]/80 bg-white/[0.05] rounded-lg">
                  {lang === "ar" ? "المزاد غير نشط للمزايدة" : "Bidding is closed"}
                </div>
              )}

              {a.buy_now_price_sar && isLive && (
                <button type="button" onClick={buyNow} disabled={busy}
                        className="w-full mt-3 py-3 rounded-full font-bold border-2 border-[#c9a55c] text-[#c9a55c] hover:bg-[#c9a55c] hover:text-[#0a0a0a] transition-colors disabled:opacity-40"
                        data-testid="buy-now-btn">
                  ⚡ {lang === "ar" ? "شراء فوري" : "Buy Now"} — {Number(a.buy_now_price_sar).toLocaleString()} ر.س
                </button>
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-4 text-[12px] text-[#525252]">
            <div className="inline-flex items-center gap-2 text-[#0a0a0a] font-bold mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              {lang === "ar" ? "حماية المشتري بنظام Escrow" : "Escrow Buyer Protection"}
            </div>
            <p className="leading-relaxed">
              {lang === "ar"
                ? "يتم حجز المبلغ بأمان حتى يستلم المشتري السيارة ويؤكد. لا تُحوَّل الأموال للبائع قبل ذلك."
                : "Funds are held safely until the buyer confirms delivery."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ lbl, val }) {
  return (
    <div className="bg-white/[0.06] rounded-lg py-2">
      <div className="text-lg font-bold">{val || 0}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#c9a55c]/70">{lbl}</div>
    </div>
  );
}
